import api from './api';

export interface FollowUp {
  id?: number;
  user_id?: number;
  user?: {
    id: number;
    display_name: string;
  };
  nation?: string;
  service?: string;
  sub_service?: string;
  customer_categories?: string;
  source?: string;
  contact_name?: string;
  contact_info?: string;
  intent?: string;
  status?: string;
  follow_date?: string;
  next_follow_date?: string;
  priority?: string;
  remark?: string;
  result?: string;
  created_at?: string;
  updated_at?: string;
  latest_record?: FollowUpRecord;
}

export interface FollowUpListParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: string;
  nation?: string;
  customer_categories?: string;
  source?: string;
  sub_service?: string;
  intent?: string;
  user_id?: number;
  sort_by?: string;
  sort_direction?: string;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface FollowUpFilterOptionsResponse {
  success: boolean;
  data: {
    nations: FilterOption[];
    customer_categories: FilterOption[];
    sources: FilterOption[];
    services: FilterOption[];
    intents: FilterOption[];
    statuses: FilterOption[];
    sellers: { value: number; label: string }[];
  };
}

export interface FollowUpListResponse {
  success: boolean;
  data: {
    data: FollowUp[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface FollowUpResponse {
  success: boolean;
  message?: string;
  data?: FollowUp;
  errors?: any;
}

export interface FollowUpRecord {
  id?: number;
  follow_up_id: number;
  remark?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

class FollowUpService {
  // 获取客户跟进列表
  async getFollowUps(params?: FollowUpListParams): Promise<FollowUpListResponse> {
    const response = await api.get('/follow-ups', { params });
    return response.data;
  }

  // 获取筛选选项
  async getFilterOptions(): Promise<FollowUpFilterOptionsResponse> {
    const response = await api.get('/follow-ups/filter-options');
    return response.data;
  }

  // 创建客户跟进
  async createFollowUp(followUpData: FollowUp): Promise<FollowUpResponse> {
    const response = await api.post('/follow-ups', followUpData);
    return response.data;
  }

  // 获取客户跟进详情
  async getFollowUp(id: number): Promise<FollowUpResponse> {
    const response = await api.get(`/follow-ups/${id}`);
    return response.data;
  }

  // 更新客户跟进
  async updateFollowUp(id: number, followUpData: FollowUp): Promise<FollowUpResponse> {
    const response = await api.put(`/follow-ups/${id}`, followUpData);
    return response.data;
  }

  // 删除客户跟进
  async deleteFollowUp(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/follow-ups/${id}`);
    return response.data;
  }

  // 批量删除跟进记录
  async batchDeleteFollowUps(ids: number[]): Promise<{ success: boolean; message: string; data?: { deleted_count: number } }> {
    const response = await api.post('/follow-ups/batch-delete', { ids });
    return response.data;
  }

  // 导出跟进记录数据
  async exportFollowUps(params?: FollowUpListParams): Promise<Blob> {
    const response = await api.get('/follow-ups-export', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
    return response.data;
  }

  // 导入跟进记录数据
  async importFollowUps(file: File): Promise<{ success: boolean; message: string; data?: any }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/follow-ups-import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  // 跟进记录：获取列表
  async getRecords(followUpId: number) {
    const response = await api.get(`/follow-ups/${followUpId}/records`);
    return response.data;
  }

  // 跟进记录：新增
  async addRecord(followUpId: number, payload: { remark?: string; status?: string }) {
    const response = await api.post(`/follow-ups/${followUpId}/records`, payload);
    return response.data;
  }

  // 跟进记录：删除
  async deleteRecord(followUpId: number, recordId: number) {
    const response = await api.delete(`/follow-ups/${followUpId}/records/${recordId}`);
    return response.data;
  }
}

const followUpService = new FollowUpService();
export default followUpService;
